import BoxMath from "./boxMath";
import { Logger } from "../../../../clients/logger";
import { RouterClient } from "../../../../clients/routerClient";
import DockingMain from "./dockingMain";
import {
	DockingBounds,
	BoundingBox,
	SnappingRegions,
	DockableMonitorParams,
	MoveRequest,
	DockableMonitorJSON,
	MonitorRectangle,
} from "./types";
import DockableWindow from "./dockableWindow";
import { MonitorInfoDetail } from "../../../../FEA";

export class DockableMonitor {
	left: number = 0;

	top: number = 0;

	right: number = 0;

	bottom: number = 0;

	dockingMain: DockingMain;

	rawMonitor: MonitorInfoDetail;

	availableRect: DockingBounds;

	bufferSize: number;

	name: string;

	bounds: BoundingBox;

	snappingRegions!: SnappingRegions;

	monitorRect!: MonitorRectangle;

	initialBounds: DockingBounds;

	deviceId?: number;

	constructor(params: DockableMonitorParams) {
		let initialBounds;

		// Pre-dockable components this function only took bounds. For backwards compatibility if the incoming params do now have a 'bounds' prop, we assume params = bounds
		if (!params.hasOwnProperty("bounds")) {
			initialBounds = params;
		} else {
			({ bounds: initialBounds } = params);
		}

		this.initialBounds = initialBounds;
		this.dockingMain = params.dockingMain;
		this.left = initialBounds.left;
		this.top = initialBounds.top;
		this.right = initialBounds.right;
		this.bottom = initialBounds.bottom;

		// Use JSON.parse/stringify to clone the monitors object, since we intend to manipulate it locally
		this.rawMonitor = JSON.parse(
			JSON.stringify(
				params.monitor instanceof DockableMonitor ? params.monitor.rawMonitor : params.monitor // Need this later in order to do docking related tasks
			)
		);

		// When docking makes a change to monitor space (claim or unclaim) docking's monitor representation is updated. The rawMonitor (which comes from launcher and is a dumbed-down functionless representation) is not updated at all. We should remove the DockableMonitor reference to rawMonitor, and instead write a function that gets called when a DockableMonitor is updated to update the rawMonitor for launcher's use.
		this.availableRect = params.monitor.availableRect;

		this.bufferSize = 15;

		this.bounds = this.getWindowBoundingBox();
		this.calculateSnappingRegions();
		this.name = initialBounds.name;
	}

	/**
	 * Determines if a window should claim monitor space. This is true when:
	 * <ul>
	 *   <li>the OS allows space to be claimed (currently Windows only)</li>
	 *   <li>claimedSpace is enabled in the config</li>
	 *   <li>the window is configured to claim space</li>
	 * </ul>
	 *
	 * @param win the window to check
	 * @returns true, if the window should claim monitor space
	 */
	#shouldClaimSpace(win: DockableWindow) {
		return (
			(this.dockingMain.isWindows &&
				this.dockingMain.serviceConfig?.claimSpace?.enabled &&
				win.win.windowOptions?.claimMonitorSpace) ??
			true
		);
	}

	/**
	 * @function {function name}
	 * @param  {type} bufferSize {description}
	 * @return {type} {description}
	 */
	setBufferSize(bufferSize: number) {
		this.bufferSize = bufferSize;
		this.bounds = this.getWindowBoundingBox();
		this.calculateSnappingRegions();
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	getWindowBoundingBox(): BoundingBox {
		return {
			min: {
				x: this.left,
				y: this.top,
			},
			max: {
				x: this.right,
				y: this.bottom,
			},
		};
	}

	/**
	 * @function {function name}
	 * @param  {type} bounds {description}
	 * @param  {type} edge   {description}
	 * @return {type} {description}
	 */
	getSnappingRegion(bounds: DockingBounds, edge: string): BoundingBox {
		const map = {
			left: {
				min: {
					x: bounds.left - this.bufferSize,
					y: bounds.top,
				},
				max: {
					x: bounds.left + this.bufferSize,
					y: bounds.bottom,
				},
			},
			right: {
				min: {
					x: bounds.right - this.bufferSize,
					y: bounds.top,
				},
				max: {
					x: bounds.right + this.bufferSize,
					y: bounds.bottom,
				},
			},
			bottom: {
				min: {
					x: bounds.left,
					y: (bounds.bottom ?? 0) - this.bufferSize,
				},
				max: {
					x: bounds.right,
					y: (bounds.bottom ?? 0) + this.bufferSize,
				},
			},
			top: {
				min: {
					x: bounds.left,
					y: (bounds.top ?? 0) - this.bufferSize,
				},
				max: {
					x: bounds.right,
					y: (bounds.top ?? 0) + this.bufferSize,
				},
			},
		};
		return map[edge];
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	calculateSnappingRegions() {
		this.snappingRegions = {
			top: this.getSnappingRegion(this.initialBounds, "top"),
			right: this.getSnappingRegion(this.initialBounds, "right"),
			bottom: this.getSnappingRegion(this.initialBounds, "bottom"),
			left: this.getSnappingRegion(this.initialBounds, "left"),
		};
	}

	/**
	 * @function {function name}
	 * @param  {type} region  {description}
	 * @param  {type} request {description}
	 * @return {type} {description}
	 */
	canSnapToRegion(region: string, request: MoveRequest): boolean {
		const innerAdjustment = 0 - this.bufferSize;
		return BoxMath.intersectBoundingBoxes(this.snappingRegions[region], {
			min: {
				x: request.snappingRegions[region].min.x - innerAdjustment,
				y: request.snappingRegions[region].min.y - innerAdjustment,
			},
			max: {
				x: request.snappingRegions[region].max.x + innerAdjustment,
				y: request.snappingRegions[region].max.y + innerAdjustment,
			},
		});
	}

	/**
	 * @function {function name}
	 * @param  {type} request {description}
	 * @return {type} {description}
	 */
	canSnapToWindow(request: MoveRequest): boolean {
		for (const region in this.snappingRegions) {
			if (this.canSnapToRegion(region, request)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Determines if supplied window (or request) is near a given edge of the monitor
	 *
	 * @param {object} request The move request
	 * @return {string} A string containing any edges of the monitor the given request is near
	 */
	getSnappedEdge(request: MoveRequest): string {
		let snappedEdge = "";
		if (request.top === this.bounds.min.y) {
			snappedEdge = `${snappedEdge}top`;
		} else if (request.bottom === this.bounds.max.y) {
			snappedEdge = `${snappedEdge}bottom`;
		}
		return snappedEdge;
	}

	/**
	 * @function {function name}
	 * @param  {type} request {description}
	 * @return {type} {description}
	 */
	snapWindow(request: MoveRequest): MoveRequest | boolean {
		const regionsToSnap = Object.keys(this.snappingRegions).flatMap((region) =>
			this.canSnapToRegion(region, request) ? [region] : []
		);
		if (!regionsToSnap) return false;

		const joinedRegionsToSnap: string = regionsToSnap.join("");

		// if req is to the right of the monitor's left edge.
		if (joinedRegionsToSnap.includes("left") && request.left >= this.snappingRegions.left.min.x) {
			request.left = this.bounds.min.x;
		}
		if (joinedRegionsToSnap.includes("top") && request.top >= this.snappingRegions.top.min.y) {
			request.top = this.bounds.min.y;
		}

		if (joinedRegionsToSnap.includes("right") && request.right <= this.snappingRegions.right.max.x) {
			// move
			if (request.changeType === 0) {
				request.left = this.bounds.max.x - request.width;
			} else {
				request.right = this.bounds.max.x;
			}
		}

		if (joinedRegionsToSnap.includes("bottom") && request.bottom <= this.snappingRegions.bottom.max.y) {
			if (request.changeType === 0) {
				request.top = this.bounds.max.y - request.height;
			} else {
				request.bottom = this.bounds.max.y;
			}
		}

		if (request.changeType === 0) {
			request.right = request.left + request.width;
			request.bottom = request.top + request.height;
		} else {
			request.width = request.right - request.left;
			request.height = request.bottom - request.top;
		}
		return request;
	}

	/**
	 * Takes any snapped edges and a given request (window) and sets the appropriate bounds of the window (when docking, the windows width is expanded to take full screen)
	 * @param {string} edge A string containing any edges the window could snap to
	 * @param {object} request The window to dock
	 * @return {object} The modified request object
	 */
	getDockedPosition(request: MoveRequest): MoveRequest | false {
		const edge = request.hasOwnProperty("snappedEdge")
			? request.snappedEdge.toLowerCase()
			: this.getSnappedEdge(request);
		request.snappedEdge = edge;

		if (request.dockableEdges && !request.dockableEdges.includes(edge)) {
			Logger.log(`Requested docked position ${edge} is not allowed by window`);
			return false;
		}

		switch (edge) {
			case "top":
				request.height = request.dockedHeight || request.height;
				delete request.dockedHeight;
				if (request.top >= this.snappingRegions.top.min.y) {
					request.top = this.bounds.min.y;
					request.width = this.bounds.max.x - this.bounds.min.x;
					request.left = this.bounds.min.x;
					request.right = this.bounds.max.x;
					request.bottom = request.top + request.height;
				}
				break;
			case "bottom":
				request.height = request.dockedHeight || request.height;
				delete request.dockedHeight;
				if (request.bottom <= this.snappingRegions.bottom.max.y) {
					request.top = this.bounds.max.y - request.height;
					request.width = this.bounds.max.x - this.bounds.min.x;
					request.left = this.bounds.min.x;
					request.right = this.bounds.max.x;
					request.bottom = this.bounds.max.y;
				}
				break;
			case "left":
				request.width = request.dockedWidth || request.width;
				delete request.dockedWidth;
				if (request.left >= this.snappingRegions.left.min.x) {
					request.left = this.bounds.min.x;
					request.height = this.bounds.max.y - this.bounds.min.y;
					request.top = this.bounds.min.y;
					request.bottom = this.bounds.max.y;
					request.right = request.left + request.width;
				}
				break;
			case "right":
				request.width = request.dockedWidth || request.width;
				delete request.dockedWidth;
				if (request.right <= this.snappingRegions.right.max.x) {
					request.left = this.bounds.max.x - request.width;
					request.height = this.bounds.max.y - this.bounds.min.y;
					request.top = this.bounds.min.y;
					request.bottom = this.bounds.max.y;
					request.right = this.bounds.max.x;
				}
				break;
			default:
				Logger.warn(`WARNING Requested docked position without providing an edge : edge = ${edge} request = `, request);
				return false;
		}

		return request;
	}

	/**
	 * Docks a window to an edge if it needs to be. Calls to update dockableMonitor/dockableWindow properties
	 *
	 * @param {object} request The move request with snappedEdge
	 * @param {object} win window to move
	 * @param {number} undockedWidth width of the window before docking
	 * @param {number} undockedHeight height of the window before docking
	 * @param {boolean} init optional - true if the dock is initiated on startup
	 */
	async dockWindowToMonitor(
		request: { snappedEdge: string },
		win: DockableWindow,
		undockedWidth: number,
		undockedHeight: number,
		init: boolean = false
	): Promise<void> {
		const claimMonitorSpace = this.#shouldClaimSpace(win);
		Logger.system.debug(
			"dockable.monitor.dockWindowToMonitor",
			`undockedWidth=${undockedWidth}`,
			`undockedHeight=${undockedHeight}`,
			`claimMonitorSpace=${claimMonitorSpace}`
		);

		if (win.justUndocked) {
			Logger.system.warn(`Prevented ${win.name} docking, because it was just undocked.`);
			// restoring undocked window bounds
			await win.setBounds({
				width: undockedWidth,
				height: undockedHeight,
				left: win.left,
				right: win.left + undockedWidth,
				top: win.top,
			});
			return;
		}

		if (claimMonitorSpace) {
			await win.setOpacity({ opacity: 0 });
		}

		if (win.previouslySnappedEdge) delete win.previouslySnappedEdge;
		if (!request.snappedEdge) return;

		let newBounds = { ...this.rawMonitor.availableRect };
		switch (request.snappedEdge) {
			case "top":
				/**
				 * When docking, the bottom value needs to account for OS reserved space. For example Windows Taskbar,
				 * the mac file menu bar or something else claiming space at the top.
				 */
				newBounds.bottom = (newBounds.top ?? 0) + win.height;
				newBounds.height = win.height;
				break;
			case "bottom":
				newBounds.top = (newBounds.bottom || 0) - win.height;
				newBounds.height = win.height;
				break;
			case "left":
				newBounds.right = newBounds.left + win.width;
				newBounds.width = win.width;
				break;
			case "right":
				newBounds.left = newBounds.right - win.width;
				newBounds.width = win.width;
				break;
			default:
				break;
		}

		win.dockedBounds = newBounds;
		await win.setBounds(newBounds);
		if (claimMonitorSpace) {
			const { err } = await RouterClient.query("Assimilation.dockWindow", {
				key: win.win.windowOptions.nativeWindowHandle,
				edge: request.snappedEdge,
				bounds: newBounds,
				init,
			});

			if (err) {
				Logger.system.error("Error querying for dock", err);
			}
		}

		win.justDocked = true;

		if (win.win.windowOptions.resizable) {
			win.win._setResizable(false);
		}

		win.win.isDocked = true;
		win.undockedPosition = {
			width: undockedWidth,
			height: undockedHeight,
			alwaysOnTop: win.win.windowOptions.alwaysOnTop,
		};

		if (this.dockingMain.serviceConfig?.claimSpace?.alwaysOnTop && !win.win.windowOptions.alwaysOnTop) {
			win.win._alwaysOnTop({ alwaysOnTop: true });
		}

		win.monitor = this;
		const edge = request.snappedEdge.trim().toUpperCase();
		if (win.monitorDockablePositions.hasOwnProperty(edge)) {
			win.dockedPosition = win.monitorDockablePositions[edge];
		}

		win.isHidden = false;

		win.saveDockableData();
	}

	/**
	 * JSON override method
	 */
	toJSON(): DockableMonitorJSON {
		return {
			bounds: this.bounds,
			availableRect: this.availableRect,
			monitorRect: this.monitorRect,
			name: this.name,
			top: this.top,
			right: this.right,
			bottom: this.bottom,
			left: this.left,
		};
	}

	/**
	 * Undocks a window
	 * @param {object} win The window to undock
	 * @param {boolean} [isClosingAction] If true, this undock is being called on a window closing
	 */
	async undockWindowFromMonitor(win: DockableWindow, isClosingAction: boolean = false) {
		Logger.system.debug("dockable.monitor.undockWindowFromMonitor");

		await RouterClient.query("Assimilation.undockWindow", {
			key: win.win.windowOptions.nativeWindowHandle,
		});

		if (win.win.windowOptions.resizable) {
			win.win._setResizable(true);
		}

		// restores the alwaysOnTop state for undocked window
		if (win.undockedPosition.alwaysOnTop !== undefined) {
			win.win._alwaysOnTop({ alwaysOnTop: win.undockedPosition.alwaysOnTop });
		}

		win.win.isDocked = false;
		// If we are undocking but not closing down Finsemble we just want to update the docking status
		// and save the dockable data.
		if (!isClosingAction) {
			win.dockedPosition = win.monitorDockablePositions.NONE;
			win.saveDockableData();
		}
	}
}

export default DockableMonitor;
